import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Ticket, Sparkles, ArrowRight, Flame } from "lucide-react";

const FALLBACK_VOUCHERS = [
  { label: "-10 % sur tout achat",  merchant: "Boulangerie du Soleil",     category: "Boulangerie",                image: "https://images.pexels.com/photos/1775043/pexels-photo-1775043.jpeg?auto=compress&w=400", percent: 10 },
  { label: "Bijou nettoyé offert",  merchant: "Bijouterie Éclat",          category: "Bijouterie · Horlogerie",    image: "https://images.pexels.com/photos/177332/pexels-photo-177332.jpeg?auto=compress&w=400",   percent: 0 },
  { label: "Café offert",           merchant: "Bistrot des Halles",        category: "Café · Restaurant",          image: "https://images.pexels.com/photos/302899/pexels-photo-302899.jpeg?auto=compress&w=400",   percent: 0 },
  { label: "-15 % parfum d'été",    merchant: "Parfumerie Provence",       category: "Bien-être · Spa",            image: "https://images.pexels.com/photos/3737576/pexels-photo-3737576.jpeg?auto=compress&w=400", percent: 15 },
  { label: "1 fougasse offerte",    merchant: "Maison Pellegrini",         category: "Pâtisserie",                 image: "https://images.pexels.com/photos/1775043/pexels-photo-1775043.jpeg?auto=compress&w=400", percent: 0 },
  { label: "-20 % livre jeunesse",  merchant: "Librairie Étang",           category: "Librairie",                  image: "https://images.pexels.com/photos/1907785/pexels-photo-1907785.jpeg?auto=compress&w=400", percent: 20 },
  { label: "Dessert offert",        merchant: "Restaurant La Calanque",    category: "Restauration",               image: "https://images.pexels.com/photos/958545/pexels-photo-958545.jpeg?auto=compress&w=400",   percent: 0 },
  { label: "1 fleur du jour",       merchant: "Fleuriste La Crau",         category: "Fleurs · Animalerie",        image: "https://images.pexels.com/photos/931177/pexels-photo-931177.jpeg?auto=compress&w=400",   percent: 0 },
];

const DEFAULT_VOUCHER_IMAGE = "https://images.pexels.com/photos/264787/pexels-photo-264787.jpeg?auto=compress&w=400";

// Match fuzzy par mot-clé dans la catégorie OU le nom du commerçant
const CATEGORY_KEYWORDS = [
  { kw: ["boulang", "patiss", "pain", "viennois"], img: "https://images.pexels.com/photos/1775043/pexels-photo-1775043.jpeg?auto=compress&w=400" },
  { kw: ["bijou", "horlog", "montre"],            img: "https://images.pexels.com/photos/177332/pexels-photo-177332.jpeg?auto=compress&w=400" },
  { kw: ["café", "cafe", "bar", "bistro"],        img: "https://images.pexels.com/photos/302899/pexels-photo-302899.jpeg?auto=compress&w=400" },
  { kw: ["restau", "table", "brasserie"],         img: "https://images.pexels.com/photos/958545/pexels-photo-958545.jpeg?auto=compress&w=400" },
  { kw: ["fleur", "plant", "jardin"],             img: "https://images.pexels.com/photos/931177/pexels-photo-931177.jpeg?auto=compress&w=400" },
  { kw: ["livre", "librair"],                     img: "https://images.pexels.com/photos/1907785/pexels-photo-1907785.jpeg?auto=compress&w=400" },
  { kw: ["parfum", "beauté", "beaute", "spa", "bien-être"], img: "https://images.pexels.com/photos/3737576/pexels-photo-3737576.jpeg?auto=compress&w=400" },
  { kw: ["vêtement", "vetement", "mode", "boutique", "prêt-à-porter"], img: "https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg?auto=compress&w=400" },
  { kw: ["coiff", "barber"],                      img: "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&w=400" },
  { kw: ["optique", "opticien", "lunett"],        img: "https://images.pexels.com/photos/701877/pexels-photo-701877.jpeg?auto=compress&w=400" },
  { kw: ["pharma", "santé", "sante", "paramedical"], img: "https://images.pexels.com/photos/3683074/pexels-photo-3683074.jpeg?auto=compress&w=400" },
  { kw: ["tabac", "presse"],                      img: "https://images.pexels.com/photos/1056553/pexels-photo-1056553.jpeg?auto=compress&w=400" },
  { kw: ["stationnement", "parking"],             img: "https://images.pexels.com/photos/63294/autos-technology-vw-multi-storey-car-park-63294.jpeg?auto=compress&w=400" },
  { kw: ["mairie", "service public"],             img: "https://images.pexels.com/photos/325185/pexels-photo-325185.jpeg?auto=compress&w=400" },
  { kw: ["sport", "fitness", "salle"],            img: "https://images.pexels.com/photos/841130/pexels-photo-841130.jpeg?auto=compress&w=400" },
];

function pickImage(category, label, merchant) {
  const hay = `${category || ""} ${label || ""} ${merchant || ""}`.toLowerCase();
  const match = CATEGORY_KEYWORDS.find((c) => c.kw.some((k) => hay.includes(k)));
  return match?.img || DEFAULT_VOUCHER_IMAGE;
}

/**
 * Bandeau de bons à gagner — affiche les sondages commerçants actifs avec
 * récompense, en double rangée infinite-scroll (directions opposées).
 */
export default function VoucherMarquee() {
  const [vouchers, setVouchers] = useState(FALLBACK_VOUCHERS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/surveys-active")
      .then(({ data }) => {
        const live = (data.items || [])
          .filter((s) => s.voucher_label && s.vouchers_remaining > 0)
          .filter((s) => !/test/i.test(s.voucher_label) && !/test/i.test(s.title || ""))
          .map((s) => ({
            label: s.voucher_label,
            merchant: s.creator_name || "Commerce d'Istres",
            category: s.merchant_category,
            // Priorité : photo personnalisée du bon → logo commerçant → photo par mot-clé → défaut
            image: s.voucher_image || s.merchant_logo || pickImage(s.merchant_category, s.voucher_label, s.creator_name),
            percent: s.voucher_percent || 0,
            survey_id: s.id,
            remaining: s.vouchers_remaining,
          }));
        if (live.length >= 3) setVouchers(live);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const row1 = [...vouchers, ...vouchers];
  const row2 = [...vouchers.slice().reverse(), ...vouchers.slice().reverse()];

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-plum-900 via-plum-800 to-plum-900 py-12 sm:py-16"
      data-testid="voucher-marquee"
    >
      {/* Decorative bokeh */}
      <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-pink-500/15 blur-[100px]" aria-hidden="true" />
      <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-sun-400/10 blur-[120px]" aria-hidden="true" />
      <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_center,_white_1px,_transparent_1px)] bg-[length:24px_24px]" aria-hidden="true" />

      <div className="relative">
        {/* Header */}
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 mb-8 sm:mb-10">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sun-400/15 text-sun-300 text-[11px] font-bold uppercase tracking-[0.18em] mb-3 border border-sun-300/20">
                <Flame size={11} strokeWidth={2.5} /> Bons en cours
              </div>
              <h2 className="font-display font-extrabold text-white text-[1.8rem] sm:text-[2.4rem] leading-[1.05] mb-2.5">
                À gagner <em className="text-sun-300 not-italic">cette semaine</em><br className="hidden sm:block" /> chez vos commerçants Istréens.
              </h2>
              <p className="text-cream-50/65 text-[14px] sm:text-[15px] leading-relaxed max-w-xl">
                Répondez à un sondage commerçant en 30 secondes — repartez avec un bon de réduction valable 90 jours en boutique.
              </p>
            </div>
            <Link
              to="/sondages"
              data-testid="voucher-marquee-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-sun-400 hover:bg-sun-300 text-plum-900 px-5 sm:px-6 py-3 font-display font-extrabold text-[14px] sm:text-[15px] shadow-xl shadow-sun-400/25 transition-all hover:-translate-y-0.5"
            >
              <Ticket size={16} strokeWidth={2.4} />
              Voir les sondages
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Stats strip */}
        {!loading && vouchers.length > 0 && vouchers[0].survey_id && (
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 mb-6 flex items-center gap-5 text-cream-50/70 text-[12px] flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <Sparkles size={12} className="text-sun-300" strokeWidth={2.4} />
              <strong className="text-white font-display">{vouchers.length}</strong> récompenses actives
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Ticket size={12} className="text-pink-300" strokeWidth={2.4} />
              <strong className="text-white font-display">
                {vouchers.reduce((sum, v) => sum + (v.remaining || 0), 0)}
              </strong> bons disponibles
            </span>
          </div>
        )}

        {/* Fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r from-plum-900 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l from-plum-900 to-transparent z-10" />

        {/* Row 1 - left direction */}
        <div className="marquee-track-1 mb-3" data-testid="voucher-marquee-row-1">
          {row1.map((v, i) => <VoucherCard key={`r1-${i}`} v={v} index={i} />)}
        </div>

        {/* Row 2 - right direction (reversed) */}
        <div className="marquee-track-2" data-testid="voucher-marquee-row-2">
          {row2.map((v, i) => <VoucherCard key={`r2-${i}`} v={v} index={i} variant="dark" />)}
        </div>
      </div>

      <style>{`
        .marquee-track-1, .marquee-track-2 {
          display: flex;
          width: max-content;
        }
        .marquee-track-1 { animation: marquee-left 60s linear infinite; }
        .marquee-track-2 { animation: marquee-right 70s linear infinite; }
        .marquee-track-1:hover, .marquee-track-2:hover { animation-play-state: paused; }
        @keyframes marquee-left {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          from { transform: translateX(-50%); }
          to   { transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track-1, .marquee-track-2 { animation: none; }
        }
      `}</style>
    </section>
  );
}

function VoucherCard({ v, index, variant }) {
  const isDark = variant === "dark";
  const Wrapper = v.survey_id ? Link : "div";
  const props = v.survey_id ? { to: `/sondages/${v.survey_id}`, "data-testid": `voucher-item-${index}` } : { "data-testid": `voucher-item-${index}` };
  return (
    <Wrapper
      {...props}
      className={`group shrink-0 mx-2 min-w-[280px] sm:min-w-[320px] rounded-2xl p-4 flex items-stretch gap-3 transition-all hover:-translate-y-1 hover:shadow-2xl ${
        isDark
          ? "bg-plum-800/60 border border-plum-700/50 backdrop-blur-sm hover:bg-plum-800/80"
          : "bg-gradient-to-br from-white to-cream-50 shadow-lg hover:shadow-pink-500/30"
      }`}
    >
      {/* Photo du commerçant ou du produit récompensé */}
      <div
        className={`relative shrink-0 w-20 sm:w-24 rounded-xl overflow-hidden ${
          isDark ? "ring-1 ring-plum-700/60" : "ring-1 ring-cream-200 shadow-sm"
        }`}
        aria-hidden="true"
      >
        <img
          src={v.image || DEFAULT_VOUCHER_IMAGE}
          alt={v.merchant}
          loading="lazy"
          onError={(e) => { e.currentTarget.src = DEFAULT_VOUCHER_IMAGE; }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {v.percent > 0 && (
          <div className="absolute top-1.5 left-1.5 z-10">
            <span className="inline-flex items-center justify-center font-display font-extrabold text-white text-[13px] sm:text-[14px] leading-none px-2.5 py-1.5 rounded-lg bg-gradient-to-br from-pink-500 to-pink-600 shadow-lg ring-2 ring-white/80">
              −{v.percent}%
            </span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/45 to-transparent" />
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
        <div>
          <div className={`font-display font-extrabold text-[14px] leading-tight ${isDark ? "text-white" : "text-plum-900"}`}>
            {v.label}
          </div>
          <div className={`text-[11px] truncate font-medium mt-0.5 ${isDark ? "text-cream-50/65" : "text-plum-700/65"}`}>
            {v.merchant}
          </div>
        </div>
        {v.survey_id && (
          <div className="flex items-center justify-between mt-1.5">
            {v.remaining ? (
              <span className={`text-[10px] font-bold ${isDark ? "text-sun-300" : "text-pink-600"}`}>
                {v.remaining} bon{v.remaining > 1 ? "s" : ""} dispo
              </span>
            ) : <span />}
            <ArrowRight size={12} className={`group-hover:translate-x-0.5 transition-transform ${isDark ? "text-sun-300" : "text-pink-500"}`} strokeWidth={2.4} />
          </div>
        )}
      </div>
    </Wrapper>
  );
}
